import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

import { ErrorFallbackBase } from "@/components/error/ErrorFallbackBase";
import { errMessage } from "@/rc/helpers";

const CHUNK_RELOAD_SESSION_KEY = "comminova:chunk-load-hmr-reload";

function isStaleChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const msg = errMessage(error);
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    msg.includes("error loading dynamically imported module")
  );
}

export function GlobalErrorFallback({ error }: { error?: unknown }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("common");

  useEffect(() => {
    if (!isStaleChunkLoadError(error)) return;
    try {
      if (sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY) === "1") return;
      sessionStorage.setItem(CHUNK_RELOAD_SESSION_KEY, "1");
      window.location.reload();
    } catch {
      /* ignore */
    }
  }, [error]);

  const handleGoHome = () => {
    navigate("/");
  };

  const handleRetry = () => {
    navigate(location.pathname + location.search, { replace: true });
  };

  return (
    <ErrorFallbackBase
      title={t("common.errors.global.title")}
      description={t("common.errors.global.description")}
      icon={<AlertCircle className="h-5 w-5 text-destructive" />}
      primaryAction={{
        label: t("common.errors.global.retry"),
        onClick: handleRetry,
      }}
      secondaryAction={{
        label: t("common.errors.global.goHome"),
        onClick: handleGoHome,
        variant: "outline",
      }}
    />
  );
}
